/**
 * InventoryStockSummaryPage — per-product aggregate stock view (INV-FG-1-1).
 *
 * Sally P0 ("mennyi van / hol / foglalt-vs-szabad"): renders the
 * `GET /inventory/stock-summaries` read-model as a per-product table.
 * Distinct from InventoryListPage which is a per-item (serial-level) list.
 *
 * Value-column (26-kör, contract-first): `totalValue` (quantity × purchasePrice)
 * az opcionális FourFieldStockSummary.totalValue mezőből. Ha a BE read-model még
 * nem adja vissza (undefined), a cella "—"-t mutat (measured-honesty).
 */

import type { FourFieldStockSummary, WarehouseStockBreakdown } from '@/api/inventory';
import { Button, Card, CardContent } from '@/components/ui';
import { useWarehouses } from '@/hooks/use-inventory';
import { useStockSummaries } from '@/hooks/use-stock-summaries';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import { DataTable, Tabs, TabsList, TabsTrigger } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type StockLevelStatus = FourFieldStockSummary['stockLevelStatus'];

const STOCK_LEVEL_COLOR: Record<StockLevelStatus, string> = {
  OK: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  LOW: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  CRITICAL: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  OUT_OF_STOCK: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const STOCK_LEVEL_LABEL_KEY: Record<StockLevelStatus, string> = {
  OK: 'inventory.stockLevel.OK',
  LOW: 'inventory.stockLevel.LOW',
  CRITICAL: 'inventory.stockLevel.CRITICAL',
  OUT_OF_STOCK: 'inventory.stockLevel.OUT_OF_STOCK',
};

export function InventoryStockSummaryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (productId: string) =>
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });

  const { warehouses } = useWarehouses();
  const { summaries, isLoading, error, refetch } = useStockSummaries({
    warehouseId: warehouseFilter || undefined,
  });

  const numCell = (value: number, className: string) => (
    <div className="text-right">
      <span className={`font-medium ${className}`}>{value}</span>
    </div>
  );

  // Phase-2 (#665): expandable per-warehouse breakdown sub-row. A raktár- és a 6
  // mennyiség-oszlop fejléce a parent-tel azonos i18n-kulcsokat használja (reuse).
  const renderBreakdown = (rows: WarehouseStockBreakdown[]) => {
    const sorted = [...rows].sort((a, b) => a.warehouseName.localeCompare(b.warehouseName, 'hu'));
    return (
      <div className="px-4 py-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('inventory.breakdown.title')}
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="py-1 text-left font-medium">{t('inventory.warehouse')}</th>
              <th className="py-1 text-right font-medium">{t('inventory.available')}</th>
              <th className="py-1 text-right font-medium">{t('inventory.reserved')}</th>
              <th className="py-1 text-right font-medium">
                {t('inventory.statusLabels.inTransit')}
              </th>
              <th className="py-1 text-right font-medium">{t('status.inService')}</th>
              <th className="py-1 text-right font-medium">{t('status.rentedOut')}</th>
              <th className="py-1 text-right font-medium">{t('inventory.total')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(wh => (
              <tr key={wh.warehouseId} className="border-t border-border/50">
                <td className="py-1 font-medium text-foreground">{wh.warehouseName}</td>
                <td className="py-1 text-right text-green-600 dark:text-green-400">
                  {wh.availableQuantity}
                </td>
                <td className="py-1 text-right text-orange-600 dark:text-orange-400">
                  {wh.reservedQuantity}
                </td>
                <td className="py-1 text-right text-blue-600 dark:text-blue-400">
                  {wh.inTransitQuantity}
                </td>
                <td className="py-1 text-right text-purple-600 dark:text-purple-400">
                  {wh.inServiceQuantity}
                </td>
                <td className="py-1 text-right text-cyan-600 dark:text-cyan-400">
                  {wh.rentedQuantity}
                </td>
                <td className="py-1 text-right font-semibold text-foreground">
                  {wh.totalQuantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const columns: ColumnDef<FourFieldStockSummary>[] = [
    {
      id: 'expand',
      header: () => null,
      cell: ({ row }) => {
        const hasBreakdown = (row.original.byWarehouse?.length ?? 0) > 0;
        if (!hasBreakdown) return null;
        const isExpanded = expandedRows.has(row.original.productId);
        return (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded ? t('inventory.breakdown.collapse') : t('inventory.breakdown.expand')
            }
            onClick={() => toggleRow(row.original.productId)}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden />
            )}
          </button>
        );
      },
    },
    {
      id: 'product',
      header: t('inventory.typeLabels.product'),
      cell: ({ row }) => (
        <p className="font-medium text-gray-900 dark:text-white">
          {row.original.productName || 'N/A'}
        </p>
      ),
    },
    {
      id: 'available',
      header: () => <div className="text-right">{t('inventory.available')}</div>,
      cell: ({ row }) =>
        numCell(row.original.availableQuantity, 'text-green-600 dark:text-green-400'),
    },
    {
      id: 'reserved',
      header: () => <div className="text-right">{t('inventory.reserved')}</div>,
      cell: ({ row }) =>
        numCell(row.original.reservedQuantity, 'text-orange-600 dark:text-orange-400'),
    },
    {
      id: 'inTransit',
      header: () => <div className="text-right">{t('inventory.statusLabels.inTransit')}</div>,
      cell: ({ row }) =>
        numCell(row.original.inTransitQuantity, 'text-blue-600 dark:text-blue-400'),
    },
    {
      id: 'inService',
      header: () => <div className="text-right">{t('status.inService')}</div>,
      cell: ({ row }) =>
        numCell(row.original.inServiceQuantity, 'text-purple-600 dark:text-purple-400'),
    },
    {
      id: 'rented',
      header: () => <div className="text-right">{t('status.rentedOut')}</div>,
      cell: ({ row }) => numCell(row.original.rentedQuantity, 'text-cyan-600 dark:text-cyan-400'),
    },
    {
      id: 'total',
      header: () => <div className="text-right">{t('inventory.total')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="text-right">
            <span className="font-semibold text-foreground">{item.totalQuantity}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400"> {item.unit}</span>
          </div>
        );
      },
    },
    {
      id: 'occupancy',
      header: () => <div className="text-right">{t('inventory.reserved')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        const occupied = item.totalQuantity - item.availableQuantity;
        return (
          <div className="text-right">
            <span className="inline-flex items-center rounded-full border border-orange-400 px-2 py-0.5 text-xs font-medium text-orange-600 dark:border-orange-500 dark:text-orange-400">
              {t('inventory.occupiedOfTotal', { occupied, total: item.totalQuantity })}
            </span>
          </div>
        );
      },
    },
    {
      id: 'stockValue',
      header: () => <div className="text-right">{t('inventory.stockValue')}</div>,
      cell: ({ row }) => {
        const val = row.original.totalValue;
        return (
          <div className="text-right">
            {val !== undefined ? (
              <span className="font-medium text-foreground">{formatCurrency(val)}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: t('table.status'),
      cell: ({ row }) => {
        const status = row.original.stockLevelStatus;
        return (
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${STOCK_LEVEL_COLOR[status] ?? 'bg-muted text-foreground'}`}
          >
            {t(STOCK_LEVEL_LABEL_KEY[status] ?? '') || status}
          </span>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('inventory.summaryTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('inventory.summarySubtitle')}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Warehouse filter tabs (per-raktár nézet, Phase-1a — multi-warehouse) */}
        <div className="mb-6 max-w-full overflow-x-auto">
          <Tabs
            value={warehouseFilter || 'all'}
            onValueChange={value => setWarehouseFilter(value === 'all' ? '' : value)}
          >
            <TabsList>
              <TabsTrigger value="all">{t('inventory.allWarehouses')}</TabsTrigger>
              {warehouses.map(wh => (
                <TabsTrigger key={wh.id} value={wh.id}>
                  {wh.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">{t('inventory.loadingStock')}</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="py-8 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">
                {t('page.errorPrefix', { message: error })}
              </p>
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            </CardContent>
          </Card>
        )}

        {/* Summary table */}
        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              <DataTable
                columns={columns}
                data={summaries}
                emptyMessage={t('inventory.noResults')}
                getRowId={item => item.productId}
                renderSubRow={row =>
                  expandedRows.has(row.productId) && (row.byWarehouse?.length ?? 0) > 0
                    ? renderBreakdown(row.byWarehouse ?? [])
                    : null
                }
              />
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
